import type { IPublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationProvider } from '@microsoft/microsoft-graph-client';
import { browserLogger } from '@newfront-insurance/dd-rum';

import { AuthOptions, type AuthState } from '../types';

export class MsalAuthenticationProvider implements AuthenticationProvider {
  private readonly pca: IPublicClientApplication;

  private readonly scopes: string[];

  private readonly redirectUri: string;

  constructor(pca: IPublicClientApplication, scopes: string[], redirectUri: string) {
    this.pca = pca;
    this.scopes = scopes;
    this.redirectUri = redirectUri;
  }

  public async acquireTokenSilent(): Promise<AuthState> {
    try {
      // getAllAccounts never throws. If there was no previous interactive login, it will return an empty array
      const accounts = this.pca.getAllAccounts();

      browserLogger.info('[MS Graph Auth] acquireTokenSilent starting', {
        accountCount: accounts.length,
        hasAccount: !!accounts[0],
        accountUsername: accounts[0]?.username,
      });

      const userAccount = await this.pca.acquireTokenSilent({
        account: accounts[0],
        scopes: this.scopes,
      });

      browserLogger.info('Acquired Office token silently');
      return {
        state: AuthOptions.LOGGED_IN,
        accessToken: userAccount.accessToken,
        userInfo: { name: userAccount.account.name, email: userAccount.account.username },
      };
    } catch (error) {
      browserLogger.info('Unable to acquire Office token silently', undefined, error as Error);
      return { state: AuthOptions.INTERACTION_REQUIRED, accessToken: null };
    }
  }

  /**
   * Clear MSAL's stuck interaction_in_progress flag from browser storage.
   * MSAL sets this flag when a popup opens and clears it on completion, but
   * if the popup is abandoned (e.g. user toggles off mid-auth) the flag can
   * get stuck, blocking all future interactive operations.
   *
   * Only targets the key for our specific client ID to avoid interfering
   * with other MSAL instances on the same origin.
   */
  private clearStuckInteractionState(): void {
    // MSAL stores the flag as: msal.<clientId>.interaction.status
    const clientId = this.pca.getConfiguration().auth.clientId;
    const key = `msal.${clientId}.interaction.status`;

    try {
      const value = localStorage.getItem(key);
      if (value) {
        browserLogger.info('[MS Graph Auth] Clearing stuck interaction state', { key });
        localStorage.removeItem(key);
      }
    } catch {
      // Storage access can fail in some environments
    }
  }

  private async acquireTokenPopup(): Promise<AuthState> {
    try {
      browserLogger.info('Trying to acquire Office token interactively');

      const userAccount = await this.pca.acquireTokenPopup({
        redirectUri: this.redirectUri,
        scopes: this.scopes,
      });

      browserLogger.info('Acquired Office token interactively.');
      return {
        state: AuthOptions.LOGGED_IN,
        accessToken: userAccount.accessToken,
        userInfo: { name: userAccount.account.name, email: userAccount.account.username },
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '';

      // If a previous popup was abandoned, MSAL's interaction flag gets stuck.
      // Clear it and retry once so the user isn't permanently locked out.
      if (errorMessage.includes('interaction_in_progress')) {
        browserLogger.info('[MS Graph Auth] Clearing stuck interaction state and retrying popup');
        this.clearStuckInteractionState();
        try {
          const userAccount = await this.pca.acquireTokenPopup({
            redirectUri: this.redirectUri,
            scopes: this.scopes,
          });
          return {
            state: AuthOptions.LOGGED_IN,
            accessToken: userAccount.accessToken,
            userInfo: { name: userAccount.account.name, email: userAccount.account.username },
          };
        } catch (retryError) {
          browserLogger.error(
            'Unable to acquire Office token interactively after retry',
            undefined,
            retryError as Error,
          );
          return { state: AuthOptions.ERROR, accessToken: null, error: retryError as Error };
        }
      }

      browserLogger.error('Unable to acquire Office token interactively', undefined, error as Error);
      return { state: AuthOptions.ERROR, accessToken: null, error: error as Error };
    }
  }

  public async login(): Promise<AuthState> {
    let authResult = await this.acquireTokenSilent();

    if (authResult.state === AuthOptions.INTERACTION_REQUIRED) {
      authResult = await this.acquireTokenPopup();
    }

    return authResult;
  }

  async getAccessToken(): Promise<string> {
    // Only attempt silent token acquisition here — never open a popup.
    // Popups should only be triggered by explicit user actions via login().
    // If silent acquisition fails the caller should handle re-authentication
    // through the OutlookConnectController flow.
    const authResult = await this.acquireTokenSilent();

    if (authResult.accessToken) {
      return authResult.accessToken;
    }

    const errorMessage =
      authResult.state === AuthOptions.INTERACTION_REQUIRED
        ? 'Office token expired — re-authentication required'
        : 'Unable to acquire Office token silently';

    browserLogger.info('[MS Graph Auth] Silent token acquisition failed, interaction required', {
      authState: authResult.state,
    });

    throw new Error(errorMessage);
  }
}

export { AuthOptions };
